import AccountingPreviewTable from './AccountingPreviewTable';
import {
  accountingPreviewAlertClass,
  accountingPreviewDetailLine,
} from './accountingPreviewUtils';

const REASON_TEMPLATES = [
  'Main reports did not match after a fuel purchase was edited — recalculating purchase cost for sales.',
  'Fuel day closing was saved under the wrong product — correcting petrol/diesel labels.',
  'Customer or bank opening balance did not match the ledger — syncing from master records.',
  'Shop stock went negative after a data entry mistake — running automatic stock correction.',
  'Cost recalculation made things worse — undoing to the previous saved state.',
  'Employee Short/Excess report reviewed and bag collection entries corrected.',
  'Fuel sales opening/closing stock reviewed and tank/day entries corrected.',
  'Gas cylinder Trade Loss/Gain showed negative GST — corrected gas ledger amounts.',
  'Gas exchange sale re-checked after ledger ID collision with shop purchase.',
  'Owners Fund mismatch reviewed — corrected BS-only or inactive-head postings on listed dates.',
  'Fuel purchase shared a transaction id with a nozzle sale — gave the purchase its own id. Amounts unchanged.',
];

const RISK_LABELS = { low: 'Low risk', medium: 'Medium risk', high: 'High risk — preview first' };
const RISK_CLASS = { low: 'info', medium: 'warning', high: 'critical' };

function fixRequiresDates(fixType) {
  return fixType?.startsWith('wac_')
    || fixType === 'nozzle_product_repair'
    || fixType === 'gas_cylinder_ledger_repair'
    || fixType === 'manual_review_gas_cylinder';
}

export default function AccountingFixWizardPanel({
  activeFix,
  preview,
  previewLoading,
  previewStale = false,
  applyReason,
  applying,
  isProcessing,
  onClose,
  onPreview,
  onApplyReasonChange,
  onApply,
  onFixChange,
}) {
  if (!activeFix) {
    return null;
  }

  return (
    <div className="admin-panel border border-primary accounting-fix-wizard-inline mt-2 mb-3">
      <div className="accounting-wizard-steps mb-3">
        <span className="step active">1 {activeFix.manual_only ? 'Checklist' : 'Preview'}</span>
        <span className="step">2 {activeFix.manual_only ? 'Confirm review' : 'Reason'}</span>
        <span className="step">3 {activeFix.manual_only ? 'Record & re-check' : 'Apply'}</span>
      </div>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="mb-1">{activeFix.label}</h5>
          {activeFix.plain_description && <p className="small text-muted mb-1">{activeFix.plain_description}</p>}
          {activeFix.what_happens && (
            <p className="small mb-1"><strong>What will happen:</strong> {activeFix.what_happens}</p>
          )}
          {activeFix.risk && (
            <span className={`admin-badge audit-severity ${RISK_CLASS[activeFix.risk]}`}>
              {RISK_LABELS[activeFix.risk] || activeFix.risk}
            </span>
          )}
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={isProcessing}>
          Close
        </button>
      </div>

      {fixRequiresDates(activeFix.fix_type) && (
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <label className="form-label small">From date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={activeFix.params.from_date || ''}
              onChange={(e) => onFixChange({ params: { from_date: e.target.value } })}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small">To date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={activeFix.params.to_date || ''}
              onChange={(e) => onFixChange({ params: { to_date: e.target.value } })}
            />
          </div>
          {previewStale && (
            <div className="col-12">
              <p className="small text-warning mb-0">Dates changed — run preview again before applying.</p>
            </div>
          )}
        </div>
      )}

      {!fixRequiresDates(activeFix.fix_type) && previewStale && (
        <p className="small text-warning mb-3">
          A reviewed value changed — click &quot;Show what will change (preview)&quot; again before applying.
        </p>
      )}

      {activeFix.params.baseline_audit_id && (
        <p className="small">Undo from saved point: <strong>#{activeFix.params.baseline_audit_id}</strong></p>
      )}

      <button type="button" className="btn btn-outline-primary btn-sm mb-3" onClick={onPreview} disabled={previewLoading || isProcessing}>
        {previewLoading ? 'Loading…' : (activeFix.manual_only ? 'Show step-by-step checklist' : 'Show what will change (preview)')}
      </button>

      {preview && (
        <div className={`admin-alert-item mb-3 ${accountingPreviewAlertClass(preview)}`}>
          <strong>{preview.message}</strong>
          {preview.manual_only && (
            <div className="small">No automatic changes — fix entries on the pump, then record review and re-check accounts.</div>
          )}
          {accountingPreviewDetailLine(preview) && (
            <div className="small">{accountingPreviewDetailLine(preview)}</div>
          )}
        </div>
      )}
      <AccountingPreviewTable
        preview={preview}
        onConfirmOverride={
          activeFix.fix_type === 'nf_stock_reconcile'
            ? (productId, targetTotalStock) => onFixChange({
              params: {
                confirm_override: { product_id: productId, target_total_stock: targetTotalStock },
              },
            })
            : undefined
        }
      />

      <div className="mt-3 mb-2">
        <label className="form-label small">
          {activeFix.manual_only
            ? 'What did you check or fix on the pump? (min 10 characters)'
            : 'Why are you applying this fix? (min 10 characters)'}
        </label>
        <div className="d-flex flex-wrap gap-1 mb-2">
          {REASON_TEMPLATES.map((t, i) => (
            <button key={i} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onApplyReasonChange(t)} title={t}>
              Use example {i + 1}
            </button>
          ))}
        </div>
        <textarea
          className="form-control"
          rows={3}
          value={applyReason}
          onChange={(e) => onApplyReasonChange(e.target.value)}
          placeholder="e.g. Profit report did not match after editing a diesel purchase on 15 May."
        />
      </div>

      <button
        type="button"
        className={activeFix.manual_only ? 'btn btn-primary' : 'btn btn-danger'}
        onClick={onApply}
        disabled={
          applying
          || previewLoading
          || previewStale
          || isProcessing
          || !preview?.status
          || (
            !activeFix.manual_only
            && preview.rows_to_change === 0
            && activeFix.fix_type !== 'wac_rollback'
          )
        }
      >
        {applying
          ? 'Saving… please wait'
          : (activeFix.manual_only ? 'Mark review complete' : 'Apply fix')}
      </button>
    </div>
  );
}
